import React from 'react';
import { connect } from 'react-redux';
import { NavLink } from 'react-router-dom';
import cn from 'classnames';

import Permitted from 'components/core/Permitted.component';
import Tooltip from 'components/generic/Tooltip.component';
import TooltipForReceptionist from 'components/TooltipForReceptionist';
import PERM from 'constants/core/perm.const';
import IN from 'constants/in';
import LINK from 'constants/link.const';
import S from 'constants/string.const';
import { useTranslation } from 'providers/GILT.provider';
import { useCurrentSalonId } from 'providers/SalonBasicInfo.provider';
import { useTheme } from 'providers/Theme.provider';
import { selectOldFlagsHideStaffManagement } from 'selectors/oldFlags.selectors';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import linkTo from 'utils/links/linkTo.util';

import CN from './SettingsMenu.module.scss';

const ForbiddenLink = ({ tag, selected, t }) => (
  <div className={CN.rel} data-bem="ForbiddenLink" data-tag={fromAnyToTrimmed(tag)}>
    <Tooltip
      variant="slickTop"
      content={`${S.noEntry} ${t('staffAndBusiness.sideMenu.tooltipPermission')} ${S.noEntry}`}
    >
      <div
        className={cn({
          [CN.menuItem]: true,
          [CN.selected]: selected,
        })}
      >
        <div className={CN.stylist} />
        {t('staffAndBusiness.sideMenu.staffAccess')}
      </div>
    </Tooltip>
  </div>
);

const SettingsMenu = ({ hideStaffManagement }) => {
  const { isDarkMode } = useTheme();
  const salonId = useCurrentSalonId();
  const { t } = useTranslation();

  const summary = linkTo({ pattern: LINK.settingsSalon, params: { salonId }, fault: '#' });
  const hours = linkTo({ pattern: LINK.salonHours, params: { salonId }, fault: '#' });
  const billing = linkTo({ pattern: LINK.billing, params: { salonId }, fault: '#' });
  const services = linkTo({ pattern: LINK.services, params: { salonId }, fault: '#' });
  const roles = linkTo({ pattern: LINK.settingRoles, params: { salonId }, fault: '#' });
  const stylist = linkTo({ pattern: LINK.settingStylist, params: { salonId }, fault: '#' });
  const permissions = linkTo({ pattern: LINK.permissions, params: { salonId }, fault: '#' });
  const settings = linkTo({ pattern: LINK.settingsCalendar, params: { id: salonId }, fault: '#' });
  const user = linkTo({ pattern: LINK.settingsUser, params: { id: salonId }, fault: '#' });
  const userNetwork = linkTo({ pattern: LINK.settingsUserNetwork, params: { id: salonId }, fault: '#' });
  const policy = linkTo({ pattern: LINK.settingsPolicy, params: { id: salonId }, fault: '#' });

  const componentClasses = cn({
    [CN.component]: true,
    [CN.darkMode]: isDarkMode,
  });

  return (
    <div data-bem="SettingsMenu" className={componentClasses}>
      <div className={CN.container}>
        <NavLink className={CN.menuItem} activeClassName={CN.selected} to={summary}>
          <div className={CN.monitor} />
          {t('staffAndBusiness.sideMenu.summary')}
        </NavLink>
        <NavLink className={CN.menuItem} activeClassName={CN.selected} to={hours}>
          <div className={CN.hours} />
          {t('staffAndBusiness.sideMenu.businessInfo')}
        </NavLink>
        <div className={CN.rel}>
          <NavLink className={CN.menuItem} activeClassName={CN.selected} to={billing}>
            <div className={CN.billing} />
            {t('staffAndBusiness.sideMenu.subscriptionAndBilling')}
          </NavLink>
          <TooltipForReceptionist where={IN.mainSettingsMenu} />
        </div>

        <NavLink className={CN.menuItem} activeClassName={CN.selected} to={userNetwork}>
          <div className={CN.network} />
          {t('staffAndBusiness.sideMenu.hardwareInformation')}
        </NavLink>

        <NavLink className={CN.menuItem} activeClassName={CN.selected} to={services}>
          <div className={CN.services} />
          {t('staffAndBusiness.sideMenu.servicePrices')}
        </NavLink>
        <NavLink className={CN.menuItem} activeClassName={CN.selected} to={roles}>
          <div className={CN.roles} />
          {t('staffAndBusiness.sideMenu.jobTitles')}
        </NavLink>

        <div className={CN.rel}>
          {hideStaffManagement ? (
            <Tooltip variant="top" content={t('staffAndBusiness.sideMenu.tooltipText')}>
              <div className={CN.menuItem} activeClassName={CN.selected}>
                <div className={CN.stylist} />
                {t('staffAndBusiness.sideMenu.staffProfilesLogins')}
              </div>
            </Tooltip>
          ) : (
            <NavLink className={CN.menuItem} activeClassName={CN.selected} to={stylist}>
              <div className={CN.stylist} />
              {t('staffAndBusiness.sideMenu.staffProfilesLogins')}
            </NavLink>
          )}
        </div>

        <Permitted value={PERM.canEditStaffPermissions} tag="SettingsMenu__can-edit-perms--all">
          <NavLink className={CN.menuItem} activeClassName={CN.selected} to={permissions}>
            <div className={CN.permissions} />
            {t('staffAndBusiness.sideMenu.staffAccess')}
          </NavLink>
        </Permitted>

        <Permitted not value={PERM.canEditStaffPermissions} tag="SettingsMenu__can-edit-perms--none">
          <ForbiddenLink tag="SettingsMenu__can-edit-perms--none" t={t} />
        </Permitted>

        <NavLink className={CN.menuItem} activeClassName={CN.selected} to={settings}>
          <div className={CN.settings} />
          {t('staffAndBusiness.sideMenu.calendarSettings')}
        </NavLink>
        <NavLink className={CN.menuItem} activeClassName={CN.selected} to={user}>
          <div className={CN.user} />
          {t('staffAndBusiness.sideMenu.emailAndPassword')}
        </NavLink>
        <NavLink className={CN.menuItem} activeClassName={CN.selected} to={policy}>
          <div className={CN.policy} />
          {t('staffAndBusiness.sideMenu.privacyPolicy')}
        </NavLink>
      </div>
    </div>
  );
};

const mapStateToProps = (state, ownProps) => ({
  ownProps,
  hideStaffManagement: selectOldFlagsHideStaffManagement(state),
});

const OnlineBookingMenuHoc = connect(mapStateToProps, null, null, { pure: false })(SettingsMenu);

export default OnlineBookingMenuHoc;
